<template>
  <div class="header">
    <div class="container-fluid">
      <div class="card mt-5">
        <div
          class="card-header d-flex justify-content-between align-items-center"
        >
          <h4 class="card-header-title mb-0">Social Learning</h4>
          <b-button variant="primary" @click="addProject">
            <span class="fe fe-file-plus"></span> Add Social Learning
          </b-button>
        </div>
        <div class="card-body">
          <div class="d-flex justify-content-between">
            <div class="col m-0 p-0 mt-1">
              <h3>All Social Learning</h3>
              <small class="text-muted" v-if="pagination.total > 0">
                Showing {{ (pagination.page - 1) * pagination.pageSize + 1 }} to
                {{
                  Math.min(
                    pagination.page * pagination.pageSize,
                    pagination.total
                  )
                }}
                of {{ pagination.total }} entries
              </small>
            </div>
            <div class="col m-0 p-0">
              <div class="d-flex gap-3 justify-content-end align-items-center">
                <div style="width: 250px">
                  <input
                    v-model="searchQuery"
                    @keyup.enter="handleSearch"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Search Social Learning..."
                  />
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="openFilterModal"
                >
                  <span class="fe fe-sliders"></span> Filter
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  @click="resetFilters"
                  title="Reset Filters"
                >
                  <span class="fe fe-refresh-cw"></span>
                </button>
              </div>
            </div>
          </div>

          <!-- Active Filters Display -->
          <div v-if="hasActiveFilters" class="d-flex flex-wrap gap-2 mt-3 mb-2">
            <b-badge
              v-if="filters.type"
              variant="info"
              class="p-2"
              @click="removeFilter('type')"
              style="cursor: pointer"
            >
              Type: {{ filters.type === "C" ? "Coaching" : "Mentoring" }}
              <i class="fe fe-x ms-1"></i>
            </b-badge>
            <b-badge
              v-if="filters.competency"
              variant="info"
              class="p-2"
              @click="removeFilter('competency')"
              style="cursor: pointer"
            >
              Competency: {{ getCompetencyName(filters.competency) }}
              <i class="fe fe-x ms-1"></i>
            </b-badge>
            <b-badge
              v-if="searchQuery"
              variant="info"
              class="p-2"
              @click="removeFilter('search')"
              style="cursor: pointer"
            >
              Search: "{{ searchQuery }}"
              <i class="fe fe-x ms-1"></i>
            </b-badge>
          </div>

          <!-- Loading State -->
          <div v-if="isLoading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2 text-muted">Loading social learning data...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="projectData.length === 0" class="text-center py-5">
            <i class="fe fe-search" style="font-size: 3rem; color: #6c757d"></i>
            <h5 class="mt-3 text-muted">No social learning found</h5>
            <p class="text-muted">
              {{
                hasActiveFilters
                  ? "Try adjusting your filters or search query."
                  : "Start by adding a new social learning."
              }}
            </p>
          </div>

          <!-- Grid View -->
          <b-row class="mt-4" v-else>
            <b-col
              v-for="(item, index) in projectData"
              :key="index"
              cols="12"
              md="6"
              lg="3"
              class="mb-4"
            >
              <b-card
                no-body
                class="h-100 shadow-sm d-flex flex-column"
                @click="openDetailModal(item)"
                style="cursor: pointer"
              >
                <b-img
                  :src="
                    item.thumbnail ||
                    'https://dummyimage.com/400x200/6c757d/fff&text=Coaching+Thumbnail'
                  "
                  :alt="item.coaching_title"
                  fluid
                  class="card-img-top mb-2"
                  style="height: 180px; object-fit: cover"
                ></b-img>

                <b-card-body class="m-0 d-flex flex-column flex-grow-1">
                  <div class="d-flex flex-wrap gap-1 mb-2">
                    <b-badge
                      :style="{
                        backgroundColor:
                          item.type === 'C' ? '#E5FBF2' : '#D7EFF6',
                        color: item.type === 'C' ? '#00AE65' : '#2E8CA7',
                        border: 'none',
                      }"
                    >
                      {{ item.type === "C" ? "COACHING" : "MENTORING" }}
                    </b-badge>
                  </div>

                  <h4 class="fw-bold flex-grow-1">{{ item.coaching_title }}</h4>
                  <div
                    class="text-muted small d-flex align-items-center mt-auto mb-0"
                  >
                    <i class="fe fe-user mr-1"></i>
                    {{ item.coach_name || "-" }}
                  </div>
                  <div v-if="item.related_competencies.length > 0" class="mt-2">
                    <b-badge
                      v-for="(comp, idx) in item.related_competencies"
                      :key="idx"
                      class="me-2 mb-1 p-2 rounded"
                      :style="{
                        backgroundColor: 'transparent',
                        border: `1px solid black`,
                        color: 'black ',
                      }"
                    >
                      {{ comp.competency_name }}
                    </b-badge>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>

          <!-- Pagination -->
          <div
            v-if="projectData.length > 0"
            class="d-flex justify-content-between align-items-center mt-4"
          >
            <div class="d-flex align-items-center">
              <span class="text-muted me-2">Show:</span>
              <b-form-select
                v-model="pagination.pageSize"
                @change="changePageSize"
                size="sm"
                style="width: 80px"
                :options="[
                  { value: 8, text: '8' },
                  { value: 16, text: '16' },
                  { value: 24, text: '24' },
                  { value: 32, text: '32' },
                ]"
              />
              <span class="text-muted ms-2">entries</span>
            </div>

            <b-pagination
              v-if="pagination.totalPages > 1"
              v-model="pagination.page"
              :total-rows="pagination.total"
              :per-page="pagination.pageSize"
              @change="changePage"
              aria-controls="coaching-table"
              size="sm"
              class="mb-0"
              first-number
              last-number
              limit="5"
            />
            <div v-else class="text-muted small">Page 1 of 1</div>
          </div>
        </div>
      </div>

      <!-- MODAL COMPONENT -->
      <AddCoaching
        :isCreateModal="isCreateModal"
        :coaches="coaches"
        :competency-data="competencyData"
        :category="category"
        :is-saving="isSaving"
        @save="saveAdd"
        @update:isCreateModal="isCreateModal = $event"
      />

      <!-- MODAL FILTER -->
      <FilterCoaching
        :showFilterModal="showFilterModal"
        :filters="filters"
        :competencyData="competencyData"
        @close="closeFilterModal"
        @apply="applyFilters"
      />

      <!-- MODAL DETAIL -->
      <DetailCoaching
        :showDetailModal="showDetailModal"
        :selectedProject="selectedProject"
        :isLoadingDetail="isLoadingDetail"
        @close="closeDetailModal"
        @edit="handleEdit"
        @delete="handleDelete"
      />

      <!-- MODAL EDIT -->
      <EditCoaching
        :isEditModal="isEditModal"
        :coaches="coaches"
        :competencyData="competencyData"
        :category="category"
        :isSaving="isSaving"
        :editData="editData"
        @update:isEditModal="isEditModal = $event"
        @update="handleUpdate"
      />
    </div>
  </div>
</template>

<script>
import AddCoaching from "./modal/AddCoaching.vue";
import EditCoaching from "./modal/EditCoaching.vue";
import FilterCoaching from "./modal/FilterCoaching.vue";
import CoachingLogic from "./coaching.logic";
import DetailCoaching from "./modal/DetailCoaching.vue";

export default {
  name: "CoachingPage",
  components: { AddCoaching, EditCoaching, FilterCoaching, DetailCoaching },
  mixins: [CoachingLogic], // logic terpisah di file lain
};
</script>

<style scoped>
.badge-truncate {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.d-flex.flex-wrap {
  row-gap: 0.25rem;
  column-gap: 0.25rem;
}
</style>
